import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Video, Users, ClipboardCheck, AlertTriangle, Calendar, ExternalLink,
  ArrowLeft, CheckCircle2, FileText, TrendingDown, Loader2, X, Save, Star,
} from "lucide-react";

const timeAgo = (iso) => {
  try {
    const diff = (new Date() - new Date(iso)) / 60000;
    if (diff < 60) return `منذ ${Math.max(1, Math.round(diff))} د`;
    if (diff < 60 * 24) return `منذ ${Math.round(diff / 60)} س`;
    return `منذ ${Math.round(diff / (60 * 24))} يوم`;
  } catch { return "—"; }
};

const timeUntil = (iso) => {
  try {
    const diff = (new Date(iso) - new Date()) / 60000;
    if (diff < 60) return `خلال ${Math.max(0, Math.round(diff))} د`;
    if (diff < 60 * 24) return `خلال ${Math.round(diff / 60)} س`;
    return `خلال ${Math.round(diff / (60 * 24))} يوم`;
  } catch { return "—"; }
};

function StatCard({ icon: Icon, label, value, tone = "teal", to }) {
  const tones = {
    teal: "bg-teal-50 text-teal-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
  };
  const Wrapper = to ? Link : "div";
  const wrapperProps = to ? { to } : {};
  return (
    <Wrapper {...wrapperProps} className={`p-4 rounded-2xl bg-white border border-cream-200 card-shadow ${to ? "hover:-translate-y-0.5 hover:border-teal-200 transition-all block" : ""}`}>
      <div className={`h-11 w-11 rounded-xl grid place-items-center mb-4 ${tones[tone]}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="text-3xl font-black text-slate-900 arabic-numerals">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </Wrapper>
  );
}

export default function MentorHome() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tasksAwaiting, setTasksAwaiting] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evalTask, setEvalTask] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [s, ses, tr, ar] = await Promise.allSettled([
        api.get("/dashboard/stats"),
        api.get("/sessions"),
        api.get("/mentor/tasks-review"),
        api.get("/mentor/at-risk"),
      ]);
      if (s.status === "fulfilled") setStats(s.value.data.stats);
      if (ses.status === "fulfilled") setSessions(ses.value.data.sessions || []);
      if (tr.status === "fulfilled") setTasksAwaiting(tr.value.data.tasks || []);
      if (ar.status === "fulfilled") setAtRisk(ar.value.data.students || []);
      if ([s, ses, tr, ar].some((result) => result.status === "rejected")) setError("تعذر تحميل بعض بيانات لوحة الموجّه؛ لا تعتمد على الأعداد حتى إعادة المحاولة.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const requestedTask = searchParams.get("task");
    if (requestedTask && tasksAwaiting.length) {
      const task = tasksAwaiting.find((item) => item.id === requestedTask);
      if (task) setEvalTask(task);
    }
  }, [searchParams, tasksAwaiting]);

  const nextSession = useMemo(() => {
    const upcoming = sessions
      .filter((x) => x.status !== "cancelled" && new Date(x.scheduled_at) > new Date())
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    return upcoming[0] || null;
  }, [sessions]);

  const pendingAssessments = useMemo(() => (
    sessions.filter((s) => s.status === "completed" && !s.notes).slice(0, 5)
  ), [sessions]);

  const assigned = stats?.assigned_students ?? 0;
  const upcoming = sessions.filter((x) => x.status !== "cancelled" && new Date(x.scheduled_at) > new Date()).length;

  return (
    <div data-testid="mentor-home">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">مساحة عمل الموجّه</div>
        <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">مرحباً {user?.name}</h1>
        <p className="mt-2 text-slate-600">جلستك القادمة، مهام بانتظار تقييمك، وطلاب يحتاجون دعمك — في نظرة واحدة.</p>
        {error && <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" role="alert">{error}<button type="button" onClick={load} className="ms-3 font-bold underline">إعادة المحاولة</button></div>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="طلابي" value={assigned} tone="teal" to="/dashboard/my-students" />
        <StatCard icon={Video} label="جلسات قادمة" value={upcoming} tone="sky" to="/dashboard/sessions" />
        <StatCard icon={ClipboardCheck} label="بانتظار تقييمك" value={tasksAwaiting.length} tone="amber" />
        <StatCard icon={AlertTriangle} label="طلاب بحاجة لدعم" value={atRisk.length} tone="rose" />
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (<div key={i} className="h-56 rounded-2xl bg-white border border-cream-200 animate-pulse" />))}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Next Session */}
          <div className="lg:col-span-1 p-4 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-900 text-white card-shadow-lg" data-testid="mentor-next-session">
            <div className="text-xs font-bold uppercase tracking-widest text-teal-100 mb-3">الجلسة القادمة</div>
            {nextSession ? (
              <>
                <h3 className="text-xl font-heading font-black leading-snug">{nextSession.title}</h3>
                <div className="mt-3 text-sm text-teal-50 space-y-1">
                  <div className="flex items-center gap-2"><Users className="h-4 w-4" /> {nextSession.student_name}</div>
                  <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /> <span>{timeUntil(nextSession.scheduled_at)} · <span className="arabic-numerals">{nextSession.duration_minutes} د</span></span></div>
                </div>
                {nextSession.agenda && <p className="mt-4 text-sm text-teal-100/90 line-clamp-3">{nextSession.agenda}</p>}
                <div className="mt-6 flex flex-wrap gap-2">
                  {nextSession.meeting_provider === "microsoft_teams" && nextSession.join_url && (
                    <a href={nextSession.join_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white text-teal-800 text-sm font-bold hover:bg-amber-50" data-testid="mentor-next-session-join">
                      <ExternalLink className="h-4 w-4" /> فتح Teams
                    </a>
                  )}
                  <Link to="/dashboard/sessions" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/25 text-white text-sm font-semibold hover:bg-white/10">كل الجلسات <ArrowLeft className="h-4 w-4" /></Link>
                </div>
              </>
            ) : (
              <div className="py-8 text-center text-teal-100">
                <Video className="h-8 w-8 mx-auto mb-2 opacity-70" />
                <div className="text-sm">لا توجد جلسات قادمة</div>
                <Link to="/dashboard/sessions" className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-teal-800 text-xs font-bold">جدولة جلسة</Link>
              </div>
            )}
          </div>

          {/* Tasks Awaiting Evaluation */}
          <div className="lg:col-span-2 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="mentor-tasks-review">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                مهام بانتظار تقييمك
                <span className="text-xs font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full arabic-numerals">{tasksAwaiting.length}</span>
              </h3>
            </div>
            <div className="divide-y divide-cream-100">
              {tasksAwaiting.map((t) => (
                <div key={t.id} className="py-3 flex items-start gap-3" data-testid={`mtask-${t.id}`}>
                  <div className="h-9 w-9 rounded-lg bg-teal-50 text-teal-700 grid place-items-center shrink-0"><FileText className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <Link to={`/dashboard/mentor/student/${t.student_id}`} className="text-teal-700 hover:underline font-semibold">{t.student_name}</Link>
                      {" "}· {timeAgo(t.created_at)}
                      {t.score != null && <span className="ms-2 arabic-numerals text-amber-700">· الجمعية: {t.score}/100</span>}
                    </div>
                  </div>
                  <button onClick={() => setEvalTask(t)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-bold hover:bg-teal-600" data-testid={`eval-btn-${t.id}`}>
                    تقييم
                  </button>
                </div>
              ))}
              {tasksAwaiting.length === 0 && (<EmptyRow icon={CheckCircle2} text="لا توجد مهام بانتظار تقييمك" />)}
            </div>
          </div>

          {/* At-Risk Students */}
          <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="mentor-at-risk">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                طلاب بحاجة لدعم
                <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full arabic-numerals">{atRisk.length}</span>
              </h3>
            </div>
            <div className="space-y-3">
              {atRisk.map((s) => (
                <Link to={`/dashboard/mentor/student/${s.id}`} key={s.id} className="block p-3 rounded-lg bg-rose-50/50 border border-rose-100 hover:bg-rose-50 transition-colors" data-testid={`atrisk-${s.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 text-sm">{s.name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{s.major || "—"}</div>
                    </div>
                    <div className="text-left shrink-0">
                      {s.attendance_pct != null && (
                        <div className="flex items-center gap-1 text-xs font-bold text-rose-700 arabic-numerals"><TrendingDown className="h-3.5 w-3.5" /> {s.attendance_pct}%</div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.reasons.map((r, i) => (<span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-rose-200 text-rose-700 font-semibold">{r}</span>))}
                  </div>
                  <div className="mt-2 text-[11px] text-teal-700 font-bold inline-flex items-center gap-1">فتح ملف الطالب <ArrowLeft className="h-3 w-3" /></div>
                </Link>
              ))}
              {atRisk.length === 0 && (<EmptyRow icon={CheckCircle2} text="كل الطلاب يتقدّمون جيداً" />)}
            </div>
          </div>

          {/* Pending Post-Session Assessments */}
          <div className="lg:col-span-2 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="mentor-pending-assessments">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                تقييمات ما بعد الجلسة
                <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full arabic-numerals">{pendingAssessments.length}</span>
              </h3>
              <Link to="/dashboard/sessions" className="text-xs font-semibold text-teal-700 hover:text-teal-800 inline-flex items-center gap-1">فتح الجلسات <ArrowLeft className="h-3.5 w-3.5" /></Link>
            </div>
            <div className="divide-y divide-cream-100">
              {pendingAssessments.map((p) => (
                <Link to="/dashboard/sessions" key={p.id} className="py-3 flex items-center gap-3 hover:bg-cream-50/60 rounded-lg px-2" data-testid={`assess-${p.id}`}>
                  <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 grid place-items-center shrink-0"><FileText className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{p.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.student_name} · انتهت {timeAgo(p.scheduled_at)}</div>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-teal-700">كتابة ملاحظات →</span>
                </Link>
              ))}
              {pendingAssessments.length === 0 && (<EmptyRow icon={CheckCircle2} text="لا توجد تقييمات معلّقة" />)}
            </div>
          </div>
        </div>
      )}

      {evalTask && <TaskEvalModal task={evalTask} onClose={() => setEvalTask(null)} onSaved={() => { setEvalTask(null); load(); }} />}
    </div>
  );
}

function EmptyRow({ icon: Icon, text }) {
  return (
    <div className="py-8 flex flex-col items-center justify-center text-center gap-2 text-slate-500">
      <Icon className="h-8 w-8 text-slate-300" />
      <div className="text-sm">{text}</div>
    </div>
  );
}

function TaskEvalModal({ task, onClose, onSaved }) {
  const [feedback, setFeedback] = useState("");
  const [score, setScore] = useState(85);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!feedback.trim()) { toast.error("اكتب ملاحظتك"); return; }
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}`, { mentor_feedback: feedback, mentor_score: Number(score) });
      toast.success("تم إرسال تقييمك للطالب");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="task-eval-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="font-heading font-bold text-slate-900 text-lg">تقييم مهمة الطالب</h3>
            <div className="text-xs text-slate-500 mt-0.5">{task.title} · {task.student_name}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          {task.student_submission && (
            <div className="p-3 rounded-lg bg-cream-50 border border-cream-200 text-sm">
              <div className="text-[11px] font-bold text-teal-700 mb-1">تسليم الطالب</div>
              <div className="text-slate-800 whitespace-pre-line">{task.student_submission}</div>
            </div>
          )}
          {task.association_feedback && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm">
              <div className="text-[11px] font-bold text-emerald-700 mb-1">ملاحظات الجمعية {task.score != null && <span className="ms-1 arabic-numerals text-amber-700">({task.score}/100)</span>}</div>
              <div className="text-slate-800 whitespace-pre-line">{task.association_feedback}</div>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">درجتك ({score}/100)</label>
            <input type="range" min={0} max={100} step={5} value={score} onChange={(e) => setScore(Number(e.target.value))} className="w-full accent-teal-700" data-testid="mentor-score" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">ملاحظات الموجّه</label>
            <textarea rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="نقاط القوة، تحسينات مقترحة، توصيات..." data-testid="mentor-feedback" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-mentor-eval">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ التقييم
          </button>
        </div>
      </div>
    </div>
  );
}
